import { assertNever, BaseNumber, promiseAll } from '@sparkdotfi/common-universal'
import { queryOptions } from '@tanstack/react-query'
import { erc20Abi, erc4626Abi } from 'viem'
import { mainnet } from 'viem/chains'
import type { Config } from 'wagmi'
import { readContract } from 'wagmi/actions'
import { z } from 'zod'

import { getChainConfigEntry } from '@/config/chain'
import { SUPPORTED_CHAIN_IDS } from '@/config/chain/constants'
import { SavingsAccountStats } from '@/config/chain/types'
import { sparkDataApiUrl } from '@/config/consts'
import { sparkVaultV2Abi } from '@/config/contracts-generated'
import { normalizedNumberSchema } from '@/domain/common/validation'
import { TokenSymbol } from '@/domain/types/TokenSymbol'

export function sparkVaultsV2StatsQueryOptionsFactory(
  type: 'mainnet-spusdc' | 'mainnet-spusdt' | 'mainnet-speth' | 'mainnet-sppyusd' | 'avalanche-spusdc',
) {
  const { savingsTokenSymbol, underlyingTokenSymbol } = getConfig(type)

  return function sparkVaultsV2StatsQueryOptions({ wagmiConfig, chainId }: { wagmiConfig: Config; chainId: number }) {
    return queryOptions<SavingsAccountStats>({
      queryKey: ['spark-vaults-v2-stats', savingsTokenSymbol, chainId],
      queryFn: async () => {
        const isSandbox = !SUPPORTED_CHAIN_IDS.includes(chainId)
        const { tokenDefinitionRepository } = getChainConfigEntry(chainId)
        const savingsToken = tokenDefinitionRepository.findOneBySymbol(savingsTokenSymbol)
        const underlyingToken = tokenDefinitionRepository.findOneBySymbol(underlyingTokenSymbol)

        const { depositCap, liquidity, totalAssets, baData } = await promiseAll(
          {
            depositCap: () =>
              readContract(wagmiConfig, {
                address: savingsToken.address,
                functionName: 'depositCap',
                args: [],
                abi: sparkVaultV2Abi,
                chainId,
              }),
            liquidity: () =>
              readContract(wagmiConfig, {
                address: underlyingToken.address,
                abi: erc20Abi,
                functionName: 'balanceOf',
                args: [savingsToken.address],
                chainId,
              }),
            totalAssets: () =>
              readContract(wagmiConfig, {
                address: savingsToken.address,
                functionName: 'totalAssets',
                args: [],
                abi: erc4626Abi,
                chainId,
              }),
            baData: () =>
              fetch(`${sparkDataApiUrl}/tokens/${savingsToken.address}/?chainid=${isSandbox ? mainnet.id : chainId}`),
          },
          { concurrency: 'unbounded' },
        )
        if (!baData.ok) {
          throw new Error('Error fetching savings stats')
        }
        const result = await baData.json()
        const parsedResult = overallReturnSchema.parse(result)
        const totalAssetsNormalized = BaseNumber(totalAssets).toNormalizedUnit(underlyingToken.decimals)
        const depositCapNormalized = BaseNumber(depositCap).toNormalizedUnit(underlyingToken.decimals)
        const liquidityNormalized = BaseNumber(liquidity).toNormalizedUnit(underlyingToken.decimals)
        const systemAvailableLiquidity = parsedResult.data.system_available_liquidity ?? undefined

        return {
          tvl: totalAssetsNormalized,
          users: parsedResult.data.number_of_wallets,
          depositCap: depositCapNormalized,
          liquidity: liquidityNormalized,
          systemAvailableLiquidity,
        }
      },
    })
  }
}

const overallReturnSchema = z.object({
  data: z.object({
    total_supply: normalizedNumberSchema,
    number_of_wallets: z.number(),
    system_available_liquidity: normalizedNumberSchema.nullable().transform((v) => v ?? undefined),
  }),
  success: z.literal(true),
})

function getConfig(
  type: 'mainnet-spusdc' | 'mainnet-spusdt' | 'mainnet-speth' | 'mainnet-sppyusd' | 'avalanche-spusdc',
): {
  savingsTokenSymbol: TokenSymbol
  underlyingTokenSymbol: TokenSymbol
} {
  switch (type) {
    case 'mainnet-spusdc':
    case 'avalanche-spusdc':
      return {
        savingsTokenSymbol: TokenSymbol('spUSDC'),
        underlyingTokenSymbol: TokenSymbol('USDC'),
      }
    case 'mainnet-spusdt':
      return {
        savingsTokenSymbol: TokenSymbol('spUSDT'),
        underlyingTokenSymbol: TokenSymbol('USDT'),
      }
    case 'mainnet-speth':
      return {
        savingsTokenSymbol: TokenSymbol('spETH'),
        underlyingTokenSymbol: TokenSymbol('WETH'),
      }
    case 'mainnet-sppyusd':
      return {
        savingsTokenSymbol: TokenSymbol('spPYUSD'),
        underlyingTokenSymbol: TokenSymbol('PYUSD'),
      }
    default:
      assertNever(type)
  }
}
